import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Button, Input } from "@/shared/ui";
import { PROFILE_PAGE_ROUTE, CHANGE_PASSWORD_PAGE_ROUTE, FORGOT_PASSWORD_PAGE_ROUTE } from "@/shared/utils";
import { useAppDispatch } from "@/shared/hooks";
import { AuthLayout } from "../components";
import { useLoginMutation } from "../api";
import { setMustChangePassword } from "../slice";
import type { LoginErrorResponse } from "../types";
import { mapBackendErrorToI18nKey } from "../utils/errorMapping";
import { type LoginFormValues, loginSchema } from "../validation";

function getLoginErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "data" in error) {
    const fetchError = error as FetchBaseQueryError;

    if (typeof fetchError.status === "number" && fetchError.status === 401) {
      if (fetchError.data && typeof fetchError.data === "object") {
        const errorData = fetchError.data as LoginErrorResponse;
        if (errorData.error && typeof errorData.error === "string") {
          return mapBackendErrorToI18nKey(errorData.error);
        }
      }
      return "errors.invalid_credentials";
    }

    if (typeof fetchError.status === "number" && fetchError.status === 429) {
      return "errors.rate_limited";
    }

    if (fetchError.data && typeof fetchError.data === "object") {
      const errorData = fetchError.data as LoginErrorResponse;

      if (errorData.error && typeof errorData.error === "string") {
        return mapBackendErrorToI18nKey(errorData.error);
      }
    }

    if (fetchError.status === "FETCH_ERROR" || fetchError.status === "PARSING_ERROR") {
      return "errors.server_unavailable";
    }

    if (typeof fetchError.status === "number" && fetchError.status >= 500) {
      return "errors.server_unavailable";
    }
  }

  return "errors.unknown";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation("LoginPage");

  const [login, { isLoading, error }] = useLoginMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  async function onSubmit(data: LoginFormValues) {
    try {
      const res = await login(data).unwrap();

      if (res.require_password_change && res.temp_token) {
        sessionStorage.setItem("temp_token", res.temp_token);
        dispatch(setMustChangePassword(true));
        navigate(CHANGE_PASSWORD_PAGE_ROUTE, { replace: true });
      } else {
        sessionStorage.removeItem("temp_token");
        dispatch(setMustChangePassword(false));
        navigate(PROFILE_PAGE_ROUTE, { replace: true });
      }
    } catch (err) {
      void err;
    }
  }

  return (
    <AuthLayout>
      <title>{t("meta.title")}</title>
      <meta name="description" content={t("meta.description")} />
      <div className="w-full">
        <form onSubmit={handleSubmit(onSubmit)} className="max-w-sm mx-auto mt-12 flex flex-col gap-6">
          <h1 className="text-center text-display-xs content-base-primary">{t("title")}</h1>
          <Input
            label={t("identifier.label")}
            placeholder={t("identifier.placeholder")}
            autoComplete="username"
            {...register("identifier")}
            error={errors.identifier?.message ? t(errors.identifier.message) : undefined}
          />
          <div className="flex flex-col gap-3">
            <Input
              type="password"
              label={t("password.label")}
              placeholder={t("password.placeholder")}
              autoComplete="current-password"
              {...register("password")}
              error={errors.password?.message ? t(errors.password.message) : undefined}
            />
            <Link
              to={FORGOT_PASSWORD_PAGE_ROUTE}
              className="self-end text-body-regular-sm underline content-base-primary cursor-pointer">
              {t("forgotPassword")}
            </Link>
          </div>

          {error && !isLoading && <p className="content-action-negative">{t(getLoginErrorMessage(error))}</p>}
          <Button size="lg" variant="primary" disabled={isLoading} type="submit">
            {isLoading ? t("loading") : t("button")}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
